/**
 * A small BM25 index for retrieval over the nest.
 *
 * An independent implementation (no `orama` / `lunr` dependency) — the corpus is
 * a phone-sized personal nest, and keeping it hand-rolled holds the bundle down
 * and lets it share `tokenize()` with the rest of the client so a query and the
 * index agree on what a term is.
 */
import { tokenize, normalizedQueryTerms } from '../shared/match-query.js';

export interface IndexDoc {
  id: string;
  /** Weighted more heavily — a title/slug hit is a strong signal. */
  title: string;
  body: string;
}

export interface Hit {
  id: string;
  score: number;
}

const K1 = 1.5;
const B = 0.75;
const TITLE_BOOST = 3;

interface Entry {
  id: string;
  tf: Map<string, number>;
  len: number;
}

export class Bm25 {
  #entries: Entry[] = [];
  #df = new Map<string, number>();
  #avgLen = 0;

  constructor(docs: IndexDoc[] = []) {
    this.rebuild(docs);
  }

  get size(): number {
    return this.#entries.length;
  }

  rebuild(docs: IndexDoc[]): void {
    this.#entries = [];
    this.#df = new Map();
    let total = 0;

    for (const doc of docs) {
      const titleTokens = tokenize(doc.title);
      const tokens = [
        ...Array.from({ length: TITLE_BOOST }, () => titleTokens).flat(),
        ...tokenize(doc.body),
      ];
      const tf = new Map<string, number>();
      for (const tok of tokens) tf.set(tok, (tf.get(tok) ?? 0) + 1);
      for (const term of tf.keys()) this.#df.set(term, (this.#df.get(term) ?? 0) + 1);
      this.#entries.push({ id: doc.id, tf, len: tokens.length });
      total += tokens.length;
    }
    this.#avgLen = this.#entries.length ? total / this.#entries.length : 0;
  }

  /** Top matches for a natural-language query, most relevant first. */
  search(query: string, limit = 10): Hit[] {
    const terms = normalizedQueryTerms(query);
    if (!terms.length || !this.#entries.length) return [];
    const n = this.#entries.length;

    const hits: Hit[] = [];
    for (const entry of this.#entries) {
      let score = 0;
      for (const term of terms) {
        const f = entry.tf.get(term);
        if (!f) continue;
        const df = this.#df.get(term) ?? 0;
        const idf = Math.log(1 + (n - df + 0.5) / (df + 0.5));
        const norm = f * (K1 + 1);
        const denom = f + K1 * (1 - B + (B * entry.len) / (this.#avgLen || 1));
        score += idf * (norm / denom);
      }
      if (score > 0) hits.push({ id: entry.id, score });
    }

    hits.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
    return hits.slice(0, limit);
  }
}
